import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaUserGraduate } from "react-icons/fa6";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import ImageUploader from "../../components/ImageUploader";
import { API_cursos, API_BASE_URL } from "../../config/apiConfig";
import "./css/NuevoEmpleo.css";
import { useAuth } from "../../context/AuthContext";

const NuevaFormacion = () => {
  const [nombreFormacion, setNombreFormacion] = useState("");
  const [sectorLaboral, setSectorLaboral] = useState("");
  const [centroImparticion, setCentroImparticion] = useState("");
  const [direccionCentro, setDireccionCentro] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [duracion, setDuracion] = useState("");
  const [destinatarios, setDestinatarios] = useState([]);
  const [modalidad, setModalidad] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [plazas, setPlazas] = useState("");
  const [linkInformacion, setLinkInformacion] = useState("");
  const [fechaInicioInscripcion, setFechaInicioInscripcion] = useState("");
  const [fechaFinInscripcion, setFechaFinInscripcion] = useState("");
  const [destacado, setDestacado] = useState(false);

  const [mostrarModal, setMostrarModal] = useState(false);
  const [foto, setFoto] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();

  const handleCancelar = () => {
    setMostrarModal(true);
  };

  const handleSalir = () => {
    navigate("/admin/formacion");
  };

  const handleDestinatarioChange = (value, checked) => {
    if (checked) {
      setDestinatarios([...destinatarios, value]);
    } else {
      setDestinatarios(destinatarios.filter((d) => d !== value));
    }
  };

  // Cargar datos cuando editamos
  useEffect(() => {
    const cargarDatos = async () => {
      if (!id) return;
      try {
        const response = await fetch(`${API_cursos}/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const curso = await response.json();

        setNombreFormacion(curso.title || "");
        setSectorLaboral(curso.categorySector || "");
        setDescripcion(curso.description || "");
        setLinkInformacion(curso.link || "");
        setDestacado(curso.isFeatured || false);
        setModalidad(curso.modality || "");
        setCentroImparticion(curso.institutionalFacility || "");
        setPlazas(curso.seats ? curso.seats.toString() : "");
        setDuracion(curso.duration ? `${curso.duration} horas` : "");

        // Manejar fechas
        if (curso.startDate) {
          const fechaInicio = new Date(curso.startDate);
          setFechaInicio(fechaInicio.toISOString().slice(0, 10));
        }
        if (curso.endDate) {
          const fechaFin = new Date(curso.endDate);
          setFechaFin(fechaFin.toISOString().slice(0, 10));
        }
        if (curso.initialRegistration) {
          const fechaInicioInsc = new Date(curso.initialRegistration);
          setFechaInicioInscripcion(fechaInicioInsc.toISOString().slice(0, 10));
        }
        if (curso.finalRegistration) {
          const fechaFinInsc = new Date(curso.finalRegistration);
          setFechaFinInscripcion(fechaFinInsc.toISOString().slice(0, 10));
        }

        // Manejar ubicación
        if (curso.location && curso.institutionalFacility) {
          const direccion = curso.location
            .replace(curso.institutionalFacility, "")
            .replace(/^,\s*/, "");
          if (direccion) {
            setDireccionCentro(direccion);
          }
        }

        // Manejar destinatarios
        if (curso.targetUsers && curso.targetUsers !== "Todos") {
          const destinatariosArray = curso.targetUsers.split(", ");
          setDestinatarios(destinatariosArray);
        } else {
          // Si es "Todos" o no está definido, seleccionar "Todos"
          setDestinatarios(["Todos"]);
        }

        // Manejar imagen
        if (curso.imageUrl && curso.imageUrl !== "/images/default-course.jpg") {
          setFoto(curso.imageUrl);
        }
      } catch (error) {
        console.error("Error al cargar el curso:", error);
        toast.error("Error al cargar los datos del curso");
      }
    };

    cargarDatos();
  }, [id]);

  const handleSubmit = async () => {
    // Validation - solo campos realmente obligatorios en la API
    if (
      !nombreFormacion ||
      !sectorLaboral ||
      !centroImparticion ||
      !fechaInicio ||
      !fechaFin ||
      !duracion ||
      !modalidad ||
      !fechaInicioInscripcion ||
      !fechaFinInscripcion ||
      !descripcion
    ) {
      toast.error("Por favor, rellena todos los campos obligatorios (*)");
      return;
    }

    if (!plazas || isNaN(plazas) || parseInt(plazas) <= 0) {
      toast.error("Por favor, introduce un número válido de plazas");
      return;
    }

    setIsSubmitting(true);

    try {
      const ubicacion = direccionCentro
        ? `${centroImparticion}, ${direccionCentro}`
        : centroImparticion;
      const destinatariosString =
        destinatarios.length > 0 ? destinatarios.join(", ") : "Todos";

      // Parse duration to number (assuming it's in hours)
      const durationNumber = parseInt(duracion.replace(/\D/g, "")) || 0;

      const courseData = {
        title: nombreFormacion,
        description: descripcion || "Sin descripción",
        categorySector: sectorLaboral,
        isFeatured: destacado,
        startDate: new Date(fechaInicio).toISOString(),
        imageUrl: foto || "/images/default-course.jpg", // Default image
        duration: durationNumber,
        endDate: new Date(fechaFin).toISOString(),
        seats: parseInt(plazas),
        initialRegistration: new Date(fechaInicioInscripcion).toISOString(),
        finalRegistration: new Date(fechaFinInscripcion).toISOString(),
        modality: modalidad,
        institutionalFacility: centroImparticion,
        link: linkInformacion,
        location: ubicacion,
        targetUsers: destinatariosString,
      };

      const url = id ? `${API_cursos}/${id}` : API_cursos;
      const method = id ? "PUT" : "POST";

      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
        body: JSON.stringify(courseData),
      });

      if (!res.ok) {
        throw new Error(`Error ${res.status}: ${res.statusText}`);
      }

      const result = await res.json();

      toast.success(
        id ? "Curso actualizado correctamente" : "Curso creado correctamente",
        { autoClose: 2000 }
      );
      setTimeout(() => navigate("/admin/formacion"), 2000);
    } catch (error) {
      console.error(
        id ? "Error al actualizar el curso:" : "Error al crear el curso:",
        error
      );
      toast.error(
        id
          ? "Error al actualizar el curso. Por favor, inténtalo de nuevo."
          : "Error al crear el curso. Por favor, inténtalo de nuevo."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      <main className="main creacion-ofertas">
        <section className="cabecera between">
          <h1 className="titulo-user align-center">
            {id ? "EDITAR CURSO" : "NUEVO CURSO"}
          </h1>
          <div className="fechas-inscripcion align-center">
            <p className="verde">PLAZOS DE INSCRIPCIÓN *:</p>
            <input
              type="date"
              value={fechaInicioInscripcion}
              onChange={(e) => setFechaInicioInscripcion(e.target.value)}
              required
            />
            <p>-</p>
            <input
              type="date"
              value={fechaFinInscripcion}
              onChange={(e) => setFechaFinInscripcion(e.target.value)}
              required
            />
          </div>
          <div className="noticias-destacadas align-center">
            <input
              type="checkbox"
              id="terminos"
              checked={destacado}
              onChange={(e) => setDestacado(e.target.checked)}
            />
            <label> Colocar en Destacados</label>
          </div>
        </section>

        <section className="formulario-alta nueva-formacion between gap-50">
          <div className="datos-generales column gap-50">
            <div className="flex-1 flex gap-25">
              <div className="campo column">
                <label className="verde mayusculas">
                  Nombre de la formación *
                </label>
                <input
                  type="text"
                  placeholder="Curso"
                  value={nombreFormacion}
                  onChange={(e) => setNombreFormacion(e.target.value)}
                  required
                />
              </div>

              <div className="campo column">
                <label className="verde mayusculas">
                  SECTOR LABORAL al que pertenece*
                </label>
                <select
                  required
                  value={sectorLaboral}
                  onChange={(e) => setSectorLaboral(e.target.value)}
                >
                  <option value="" disabled>
                    Sector
                  </option>
                  <option value="AFD">
                    Actividades Físicas y Deportivas (AFD)
                  </option>
                  <option value="ACT">
                    Actividades y Competencias Transversales (ACT)
                  </option>
                  <option value="ADG">Administración y Gestión (ADG)</option>
                  <option value="AGA">Agraria (AGA)</option>
                  <option value="ARG">Artes Gráficas (ARG)</option>
                  <option value="ART">Artes y Artesanías (ART)</option>
                  <option value="COM">Comercio y Marketing (COM)</option>
                  <option value="EOC">Edificación y Obra Civil (EOC)</option>
                  <option value="ELE">Electricidad y Electrónica (ELE)</option>
                  <option value="ENA">Energía y Agua (ENA)</option>
                  <option value="FME">Fabricación Mecánica (FME)</option>
                  <option value="HOT">Hostelería y Turismo (HOT)</option>
                  <option value="IMP">Imagen Personal (IMP)</option>
                  <option value="IMS">Imagen y Sonido (IMS)</option>
                  <option value="INA">Industrias Alimentarias (INA)</option>
                  <option value="IEX">Industrias Extractivas (IEX)</option>
                  <option value="IFC">
                    Informática y Comunicaciones (IFC)
                  </option>
                  <option value="IMA">Instalación y Mantenimiento (IMA)</option>
                  <option value="IAD">
                    Inteligencia Artificial y Data (IAD)
                  </option>
                  <option value="MAM">Madera, Mueble y Corcho (MAM)</option>
                  <option value="MAP">Marítimo-Pesquera (MAP)</option>
                  <option value="QUI">Química (QUI)</option>
                  <option value="SAN">Sanidad (SAN)</option>
                  <option value="SEA">Seguridad y Medio Ambiente (SEA)</option>
                  <option value="SSC">
                    Servicios Socioculturales y a la Comunidad (SSC)
                  </option>
                  <option value="TCP">Textil, Confección y Piel (TCP)</option>
                  <option value="TMV">
                    Transporte y Mantenimiento de Vehículos (TMV)
                  </option>
                  <option value="VIC">Vidrio y Cerámica (VIC)</option>
                </select>
              </div>
            </div>

            <div className="between gap-25">
              <div className="campo column centro">
                <label className="verde mayusculas">
                  Centro de impartición *
                </label>
                <input
                  type="text"
                  placeholder="Nombre del centro"
                  value={centroImparticion}
                  onChange={(e) => setCentroImparticion(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Dirección del centro"
                  value={direccionCentro}
                  onChange={(e) => setDireccionCentro(e.target.value)}
                />
              </div>

              <div className="campo column w-200">
                <label className="verde mayusculas">Fechas *</label>
                <div className="align-center gap-5">
                  <p className="verde">Inicio:</p>
                  <input
                    type="date"
                    aria-label="Fecha de inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    required
                  />
                </div>
                <div className="align-center gap-5">
                  <p className="verde fin">Fin:</p>
                  <input
                    type="date"
                    aria-label="Fecha fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="campo column w-120">
                <label className="verde mayusculas">Duración *</label>
                <input
                  type="text"
                  placeholder="90 horas"
                  aria-label="Duración del curso (en horas)"
                  value={duracion}
                  onChange={(e) => setDuracion(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="campo column w-100">
              <label className="verde mayusculas mb-10">Destinatarios</label>
              <div className="wrap gap-10-20">
                {[
                  { id: "todos", label: "Genérico (todos)", value: "Todos" },
                  {
                    id: "desempleados",
                    label: "Desempleados",
                    value: "Desempleados",
                  },
                  { id: "ocupados", label: "Ocupados", value: "Ocupados" },
                  {
                    id: "diversidad-funcional",
                    label: "Diversidad funcional",
                    value: "Diversidad funcional",
                  },
                  {
                    id: "inmigrantes",
                    label: "Inmigrantes",
                    value: "Inmigrantes",
                  },
                  { id: "jovenes", label: "Jóvenes", value: "Jóvenes" },
                  {
                    id: "sngj",
                    label: "SNGJ (Jóvenes hasta 29 años)",
                    value: "SNGJ",
                  },
                  { id: "mujeres", label: "Mujeres", value: "Mujeres" },
                  {
                    id: "mayores",
                    label: "Mayores de 45",
                    value: "Mayores de 45",
                  },
                  { id: "otros", label: "Otros", value: "Otros" },
                ].map((destinatario) => (
                  <div
                    className="checkbox align-center gap-5"
                    key={destinatario.id}
                  >
                    <input
                      type="checkbox"
                      id={destinatario.id}
                      name={destinatario.id}
                      value={destinatario.value}
                      aria-label={destinatario.value}
                      checked={destinatarios.includes(destinatario.value)}
                      onChange={(e) =>
                        handleDestinatarioChange(
                          destinatario.value,
                          e.target.checked
                        )
                      }
                    />
                    <label htmlFor={destinatario.id}>
                      {destinatario.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="contenido column w-450 gap-30">
            <div className="campo column">
              <label className="verde mayusculas">Modalidad *</label>
              <div className="flex gap-20">
                <label>
                  <input
                    type="radio"
                    name="modalidad"
                    value="Presencial"
                    checked={modalidad === "Presencial"}
                    aria-label="Presencial"
                    onChange={(e) => setModalidad(e.target.value)}
                    required
                  />{" "}
                  Presencial
                </label>

                <label>
                  <input
                    type="radio"
                    name="modalidad"
                    value="Online"
                    checked={modalidad === "Online"}
                    aria-label="Online"
                    onChange={(e) => setModalidad(e.target.value)}
                    required
                  />{" "}
                  Online
                </label>

                <label>
                  <input
                    type="radio"
                    name="modalidad"
                    value="Semipresencial"
                    checked={modalidad === "Semipresencial"}
                    aria-label="Semipresencial"
                    onChange={(e) => setModalidad(e.target.value)}
                    required
                  />{" "}
                  Semipresencial
                </label>
              </div>
            </div>

            <div className="campo column sobre-mi">
              <label className="verde mayusculas">
                DESCRIPCIÓN DEL curso *
              </label>
              <textarea
                placeholder="Breve descripción..."
                aria-label="Descripción sobre el curso"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
              />
            </div>

            <ImageUploader
              imageUrl={foto}
              onImageChange={setFoto}
              folder="formaciones"
              label="Foto del curso"
            />

            <div className="between ">
              <div className="campo column">
                <label className="verde mayusculas">Plazas *</label>
                <input
                  className="w-65"
                  type="number"
                  placeholder="15"
                  aria-label="Número de plazas"
                  value={plazas}
                  onChange={(e) => setPlazas(e.target.value)}
                  required
                />
              </div>

              <div className="campo column">
                <label className="verde mayusculas">
                  Link a la información 
                </label>
                <input
                  className="w-350"
                  type="text"
                  placeholder="https://www.oferta_empleo.com"
                  aria-label="Link a la información sobre el curso"
                  value={linkInformacion}
                  onChange={(e) => setLinkInformacion(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {mostrarModal && (
          <div className="ventana-cancelar fondo-blanco">
            <div className="column">
              <p>
                {id
                  ? "Si sales de esta página se perderán los cambios no guardados."
                  : "Si sales de esta página no se creará el anuncio del programa formativo."}
                <br></br>¿Estás seguro que quieres salir?
              </p>
              <div className="botones flex gap-20">
                <button className="fondo-rojo blanco" onClick={handleSalir}>
                  Salir
                </button>
                <button
                  className="seguir blanco fondo-verde"
                  onClick={() => setMostrarModal(false)}
                >
                  {id ? "Seguir editando" : "Seguir creando formación"}
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="botones flex-center">
          <button
            className="fondo-rojo mayusculas blanco"
            onClick={handleCancelar}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            className="descargar mayusculas fondo-verde blanco"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? id
                ? "Guardando..."
                : "Creando..."
              : id
              ? "Guardar cambios"
              : "Crear nueva formación"}
          </button>
        </section>
      </main>
    </>
  );
};

export default NuevaFormacion;
